import { useEffect, useState } from "react";
import { gfInvoiceUrl, updateGfDeal } from "../../../../api/giftingClient";
import { fmtDateTime } from "../../../../lib/giftingRules";
import type { GfDealDetail, GfPaymentStatus } from "../../../../types/gifting";
import { PAYMENT_STATUSES, PAYMENT_STATUS_LABEL, errMsg } from "../../gfUtil";
import { GfButton, GfField, GfTag } from "../../ui/GfUi";

/** 有償案件の報酬: 報酬額・支払予定月・支払状態・請求書ダウンロード */
export function PaymentSection({
  detail,
  onChanged,
  toast,
}: {
  detail: GfDealDetail;
  onChanged: () => Promise<void>;
  toast: (m: string) => void;
}) {
  const [fee, setFee] = useState(detail.fee_amount === null ? "" : String(detail.fee_amount));
  const [month, setMonth] = useState(detail.payment_due_month ?? "");
  const [status, setStatus] = useState<GfPaymentStatus>(detail.payment_status);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setFee(detail.fee_amount === null ? "" : String(detail.fee_amount));
    setMonth(detail.payment_due_month ?? "");
    setStatus(detail.payment_status);
  }, [detail.fee_amount, detail.payment_due_month, detail.payment_status]);

  const feeNum = fee.trim() === "" ? null : Number(fee);
  const invalid = feeNum !== null && (!Number.isFinite(feeNum) || feeNum < 0);
  const dirty =
    feeNum !== detail.fee_amount || (month || null) !== (detail.payment_due_month || null) || status !== detail.payment_status;
  const invoice = gfInvoiceUrl(detail.id);

  const save = async () => {
    if (invalid) return toast("報酬額は0以上の数値で入力してください");
    setBusy(true);
    try {
      await updateGfDeal(detail.id, { fee_amount: feeNum, payment_due_month: month || null, payment_status: status });
      toast("支払情報を保存しました");
      await onChanged();
    } catch (e) {
      toast(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="gf-panel-section">
      <div className="gf-panel-section-title">報酬（有償）</div>
      <div className="gf-form-grid">
        <GfField label="報酬額（円）">
          <input className="gf-input is-mono" inputMode="numeric" value={fee} onChange={(e) => setFee(e.target.value)} />
        </GfField>
        <GfField label="支払予定月">
          <input className="gf-input is-mono" type="month" value={month} onChange={(e) => setMonth(e.target.value)} />
        </GfField>
      </div>
      <GfField label="支払状態">
        <select className="gf-select" value={status} onChange={(e) => setStatus(e.target.value as GfPaymentStatus)}>
          {PAYMENT_STATUSES.map((s) => (
            <option key={s} value={s}>
              {PAYMENT_STATUS_LABEL[s]}
            </option>
          ))}
        </select>
      </GfField>
      <div className="gf-actions">
        <GfButton size="sm" variant="primary" disabled={busy || !dirty || invalid} onClick={() => void save()}>
          保存
        </GfButton>
        {detail.invoice_filename ? (
          invoice ? (
            <a className="gf-btn is-sm" href={invoice} download>
              請求書をダウンロード
            </a>
          ) : (
            <GfButton size="sm" disabled title="mock では請求書をダウンロードできません">
              請求書をダウンロード
            </GfButton>
          )
        ) : (
          <GfTag tone="muted">請求書 未受領</GfTag>
        )}
      </div>
      {detail.invoice_received_at && <div className="gf-note">請求書受領 {fmtDateTime(detail.invoice_received_at)}</div>}
      {detail.paid_at && <div className="gf-note">支払日 {fmtDateTime(detail.paid_at)}</div>}
    </section>
  );
}
